import { US_STATES } from '../../constant/US_STATES'
import toStateSpecialAbbreviation from '../../util/toStateSpecialAbbreviation'
import { CI, HEALTH_INFORMATION_SEGMENTS } from '../../constant/HEALTH_INFORMATION_SEGMENTS'
import toMMddyyyy_slashes from '../../util/toMMddyyyy_slashes'

const toUsState = (stateAbbr, specialAbbreviation = false, groupType = null) => {
  return {
    abbreviation: specialAbbreviation ? toStateSpecialAbbreviation(stateAbbr, groupType) : stateAbbr,
    name: US_STATES.filter(state => state.value === stateAbbr)[0]?.text
  }
}

const isDateField = (s) => {
  return s.toLowerCase().endsWith('date')
}

const payloadValue = (field) => {
  if (!field.maskOptions) {
    return typeof field.value === 'string' ? field.value.trim() : field.value
  } else {
    const cleanedValue = field.maskOptions.isNumber ? field.value?.replace(/,/g, '') : field.value
    return field.maskOptions.maskOnSubmit ? cleanedValue : cleanedValue?.replace(/\D/g, '')
  }
}

const digForValue = (field, isDate) => {
  if ('value' in field) {
    return (isDate && field.maskOptions?.maskOnSubmit) ? toMMddyyyy_slashes(field.value) : payloadValue(field) || field.default
  } else {
    return Array.isArray(field) ?
      field.forEach(f => digForValue(f, isDateField(f))) :
      Object.keys(field).reduce((o, k) => {
        return {
          ...o,
          ...(field[k].include && {
            [k]: ['state', 'birthState'].includes(k) ?
              toUsState(field[k].value) :
              digForValue(field[k], isDateField(k))
          })
        }
      }, {})
  }
}

const mapDeliveryDate = (healthQuestions) => {
  const pregnancyQuestion = Object.keys(healthQuestions).find(q => 'deliveryDate' in healthQuestions[q])
  return pregnancyQuestion ? toMMddyyyy_slashes(healthQuestions[pregnancyQuestion].deliveryDate.value) : ''
}

const toEoiApplicationPayload = (formState, finalize = false) => {
  return {
    verification: {},
    ...['employer', 'employee', 'dependents', 'coverageAmount', finalize && 'verification'].filter(Boolean).reduce((eoiPayload, segment) => {
      return {
        ...eoiPayload,
        [segment]: Object.keys(formState.segments[segment].fields).reduce((o, k) => {
          return {
            ...o,
            ...(formState.segments[segment].fields[k].include &&
              { [k]: digForValue(formState.segments[segment].fields[k], isDateField(k)) }
            ),
            ...(Array.isArray(formState.segments[segment].fields[k]) &&
              {
                [k]: formState.segments[segment].fields[k].map(arrayItem => {
                  return digForValue(arrayItem)
                })
              }
            )
          }
        }, {})
      }
    }, {}),
    app: {
      externalAppIdentifier: formState.context.claimKey.value,
      life: formState.options.life.value,
      disability: formState.options.disability.value,
      ci: formState.options.ci.value,
      state: toUsState(formState.segments.employer.fields.address.state.value, true, formState.options.groupType.value),
      spouseOnly: formState.options.applicantType.value === 'SP_ONLY' ? 'yes' : 'no',
      prepopId: formState.options.prepopId || ''
    },
    healthQuestions: HEALTH_INFORMATION_SEGMENTS.filter(segment => formState.segments[segment].enabled).reduce((allHealthQuestionsObject, segment) => {
      const o = {
        ...allHealthQuestionsObject,
        [segment]: {
          deliveryDate: mapDeliveryDate(formState.segments[segment].fields.healthQuestions),
          medicalConditions: [],
          medications: [],
          ...(formState.options.applicantType.value !== 'SP_ONLY' && {
            employee: {
              health: [],
              insurance: []
            }
          }),
          ...(formState.segments.dependents.fields.spouseApplying.value === 'yes' && {
            spouse: {
              health: [],
              insurance: []
            }
          }),
          ...(segment !== CI && formState.context.childrenIncluded && formState.segments.dependents.fields.childrenApplying.value === 'yes' && {
            children: {
              health: [],
              insurance: []
            }
          }),
        }
      }
      Array.of(
        { input: 'healthQuestions', output: 'health' },
        { input: 'healthInsuranceQuestions', output: 'insurance' }
      ).forEach(i => {
        Object.keys(formState.segments[segment].fields[i.input]).forEach(q => {
          ['medications', 'medicalConditions'].forEach(additionalFields => {
            o[segment][additionalFields].push(formState.segments[segment].fields[i.input][q][additionalFields].map((medication, index) => {
              return Object.keys(medication).reduce((subObject, subIndex) => {
                return {
                  ...subObject,
                  [subIndex]: digForValue(formState.segments[segment].fields[i.input][q][additionalFields][index][subIndex])
                }
              }, additionalFields === 'medicalConditions' ? { questionNumber: q.replace('q', '') } : {})
            }))
          })
          Object.keys(o[segment])
            .filter(s => ['children', 'spouse', 'employee'].includes(s))
            .forEach(applicant => {
              o[segment][applicant][i.output].push({
                id: q,
                ...(formState.segments[segment].fields[i.input][q].type !== 'choice' && {
                  answer: formState.segments[segment].fields[i.input][q][applicant].value
                }),
                ...(formState.segments[segment].fields[i.input][q].type === 'choice' && {
                  choices: Object.keys(formState.segments[segment].fields[i.input][q].choices).map(c => {
                    return {
                      id: c,
                      answer: formState.segments[segment].fields[i.input][q].choices[c][applicant].value ? 'yes' : 'no'
                    }
                  })
                }),
              })
            })
        })
      })

      o[segment].medications = o[segment].medications.flat()
      o[segment].medicalConditions = o[segment].medicalConditions.flat()
      return o
    }, {})
  }
}
export default toEoiApplicationPayload